import { Box, Button, Dialog, IconButton, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'
import { useState } from 'react'

import { Icons, formatDate, getTransformedGroupDetails, time24to12 } from '../../../utils/utilities'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import CustomTable from '../../common/custom-table/CustomTable'
import { useUserStore } from '../../../store/user.store'
import ConfirmDialog from '../../common/ConfirmDialog'
import api from '../../../service/api'



const scoresColumns = [
    { name: "Name", field: "student_name", renderCell: row => row?.student_details?.name, sortable: true, filterable: true, align: "left", padding: "10px 20px" },
    { name: 'Student ID', field: "student_id", sortable: true, filterable: true, align: "left" },
    { name: 'Total Marks', field: "max_score", align: "center" },
    { name: "Scored Marks", field: "total_score", sortable: true, filterable: true, align: "center", padding: "10px 20px" },
]

const OnlineSessionQuizDetails = observer( ( { handleClose, openState, quizInstanceDetails } ) => {

    const [scores, setScores] = useState( [] )
    const [mode, setMode] = useState( "scores" )
    const [deleting, setDeleting] = useState( false )
    const [loadingScores, setLoadingScores] = useState( false )
    const [fetchingInstances, setFetchingInstances] = useState( false )
    const [selectedQuizInstance, setSelectedQuizInstance] = useState( null )
    const [selectedInstanceDetails, setSelectedInstanceDetails] = useState( null )
    const [deleteQuizInstanceConfirmDialogState, setDeleteQuizInstanceConfirmDialogState] = useState( false )

    const UserStore = useUserStore()

    const { palette, border, table, layout } = useTheme()
    const navigate = useNavigate()

    const backToInstances = () => {
        setMode( "scores" )
        setSelectedQuizInstance( null )
        setSelectedInstanceDetails( null )
    }

    const deleteInstance = async () => {
        setDeleting( true )
        try {
            await api.quiz.deleteInstance( quizInstanceDetails.quiz_id, quizInstanceDetails.id )
            setDeleteQuizInstanceConfirmDialogState( false )
            await fetchQuizInstances()
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Something went wrong while deleting the quiz instance" )
        } finally {
            setDeleting( false )
        }
    }

    const viewScores = useCallback( async () => {
        setSelectedQuizInstance( quizInstanceDetails.id )
        setMode( 'scores' )
        setLoadingScores( quizInstanceDetails.id )
        setSelectedInstanceDetails( quizInstanceDetails )
        if ( quizInstanceDetails?.quiz_id && quizInstanceDetails?.id ) {
            try {
                const { data: { scores } } = await api.quiz.fetchQuizInstanceScores( quizInstanceDetails.quiz_id, quizInstanceDetails.id )
                setScores( scores )
                setSelectedInstanceDetails( quizInstanceDetails )
            } catch ( err ) {
                setSelectedQuizInstance( null )
            } finally {
                setLoadingScores( null )
            }
        }
    }, [quizInstanceDetails] )

    const fetchQuizInstances = useCallback( async () => {
        setFetchingInstances( true )
        try {
            await viewScores()
        } catch ( err ) {
            console.log( err )
        } finally {
            setFetchingInstances( false )
        }
    }, [viewScores] )

    useEffect( () => {
        fetchQuizInstances()
    }, [fetchQuizInstances] )


    return (
        <Dialog open={openState} slotProps={{
            paper: { sx: { width: "90%", maxWidth: "1200px", display: "flex", flexDirection: "column" } }
        }}>

            {/* Quiz Instance Delete Confirmation Dialog */}
            <ConfirmDialog status={deleteQuizInstanceConfirmDialogState} enableSecondaryConfirmation secondaryConfirmationText="ConfirmDelete" message={`All the participation details associated with this instances will be deleted!`} waiting={deleting} confirmAction={deleteInstance} cancelAction={() => setDeleteQuizInstanceConfirmDialogState( false )} />


            <Box sx={{ zIndex: 10 }} borderBottom={border[1]} position="sticky" top="0" bgcolor={palette.background.paper} display="flex" alignItems="center" flexWrap="wrap" justifyContent="space-between" padding="10px 20px" >
                <Box>
                    <Typography variant="h1" fontWeight="800" component="h2" fontSize="16px">{quizInstanceDetails?.title || "Class Quiz Participation Details"}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap="10px">
                    {!["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role ) && <Button color='greyed' sx={{ ":hover": { color: palette.customThemeColor.main } }} endIcon={Icons.default.OpenInNewIcon} onClick={() => navigate( `/faculty/quizzes/${quizInstanceDetails.quiz_id}?selectedQuizInstance=${quizInstanceDetails.id}` )}>View in Quiz Page</Button>}
                    <IconButton onClick={() => handleClose()}>{Icons.default.CloseIcon}</IconButton>
                </Box>
            </Box>

            <Box padding="20px" display="flex" minWidth={0} width="100%" flexDirection="column" gap={layout.sectionsGap}>
                {/* <Box>
                    <Typography gutterBottom display="block" variant='subtitle2' color="textSecondary">Topics and number of question</Typography>
                    <Box display="flex" flexWrap="wrap" gap="5px">
                        {quizInstanceDetails.question_bank_topics && quizInstanceDetails.question_bank_topics.map( ( topic, index ) => (
                            <Chip key={topic.id} label={`${topic.name} (${topic.question_count})`} sx={{ marginRight: "5px", borderRadius: "5px", border: border[1], bgcolor: palette.contentBg, fontWeight: "600" }} />
                        ) )}
                    </Box>
                </Box> */}
                {!fetchingInstances && mode === "scores" && selectedQuizInstance && <Box border={border[1]} borderRadius={layout.borderRadius} overflow="hidden" bgcolor={palette.background.paper}>
                    <Box borderBottom={border[1]} display="flex" flexWrap="wrap" padding="10px 20px" bgcolor={palette.contentBg} color={table.headerColor} alignItems="center" gap={layout.sectionsGap}>
                        <Box flexGrow={1}>
                            <Typography variant='subtitle1' color='textSecondary' ><small>{formatDate( selectedInstanceDetails?.start_date, { format: "ll" } )}</small> </Typography>
                            {selectedInstanceDetails?.access_group && <Typography marginTop="-5px" fontSize="14px" >   {getTransformedGroupDetails( selectedInstanceDetails?.access_group )?.chip_label}, {getTransformedGroupDetails( selectedInstanceDetails?.access_group )?.academic_details}</Typography>}
                        </Box>
                        <Typography variant='subtitle2' > <strong>{scores?.length || 0} participants</strong> </Typography>
                        {/* <Button variant='text' onClick={() => setDeleteQuizInstanceConfirmDialogState( true )} color="errorMessage" startIcon={Icons.default.DeleteIconOutlined} sx={{ textTransform: "capitalize" }}>Delete</Button> */}
                    </Box>
                    <Box>
                        {loadingScores && <DataLoadingSpinner waitingMessage="Loading scores..." />}

                        {!loadingScores && scores.length > 0 && <CustomTable containerStyles={{ border: "none", maxHeight: "350px" }} columns={scoresColumns} data={scores} dataIdKey="id" />}

                        {!loadingScores && scores.length === 0 && <Typography padding="20px" textAlign="center" display="block" variant='subtitle2'  >
                            No participations for this quiz!
                        </Typography>}
                    </Box>
                </Box>}

                {!loadingScores && mode === "scores" && scores.length === 0 && !quizInstanceDetails?.id && <Typography padding="20px" textAlign="center" display="block" variant='subtitle2'  >
                    No participations for this quiz!
                </Typography>}

                {fetchingInstances && <DataLoadingSpinner waitingMessage="Loading the quiz instances please wait..." />}
            </Box>
        </Dialog>
    )
} )

export default OnlineSessionQuizDetails
